import * as React from 'react';
import AppTabs from '../../../components/AppTabs';
import { FC, useState, useEffect, useMemo } from 'react';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import ButtonsContainer from '../../../components/ButtonsContainer';
import { ServiceSettings, FeatureEnum } from '../../../models/api-models';
import { useSelector } from 'react-redux';
import { getFeatures } from '../../../store/session/selectors';
import { getGeneralSettings } from '../../../store/settings/selectors';
import { Tabs, Tab } from '@material-ui/core';
import ServiceConfigurationSettingsTab from './ServiceConfigurationSettingsTab';
import ServiceConfigurationPricesTab from './ServiceConfigurationPricesTab';
import ServiceConfigurationStockTab from './ServiceConfigurationStockTab';
import ServiceConfigurationInvoicesTab from './ServiceConfigurationInvoicesTab';
import AppButton from '../../../components/AppButton';

export interface ServiceConfigurationModalProps {
    serviceName?: string;
    defaultValue?: ServiceSettings;
    clientOrigins: string[];
    rooms: string[];
    categories: string[];
    existingServiceNames: string[];
    onClose?: () => void;
    onSave: (serviceName: string, value: ServiceSettings) => void;
    onDelete: (serviceName: string) => void;
}

const toDomSafeIdPart = (value?: string): string => {
    const normalized = (value ?? 'new')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_-]+/g, '-')
        .replace(/^-+|-+$/g, '');

    return normalized || 'new';
};

const ServiceConfigurationModal: FC<ServiceConfigurationModalProps> = ({
    serviceName,
    defaultValue,
    clientOrigins,
    rooms,
    categories,
    existingServiceNames,
    onClose,
    onSave,
    onDelete,
}) => {
    const t = useTranslations();
    const features = useSelector(getFeatures);
    const hasStockFeature = features.includes(FeatureEnum.Stock);
    const hasInvoicesFeature = features.includes(FeatureEnum.Invoices);
    const paymentMethods = useSelector((state: Parameters<typeof getGeneralSettings>[0]) => getGeneralSettings(state)?.paymentMethods ?? []);

    const [tab, setTab] = useState<'settings' | 'prices' | 'stock' | 'invoices'>('settings');
    const fallbackServiceName = (defaultValue?.name ?? serviceName ?? '').trim();

    const [data, setData] = useState<ServiceSettings>(() => ({
        defaultDurationMinutes: 60,
        defaultPrice: 0,
        defaultPriceByOrigin: {},
        defaultRooms: [],
        description: '',
        stockItems: [],
        invoiceItemDetails: undefined,
        sendInvoiceBeforeAppointment: false,
        sendInvoiceDaysBeforeAppointment: 3,
        sendReceiptOnPayment: false,
        sendReceiptOnAllPaymentMethods: true,
        sendReceiptOnPaymentMethods: [],
        sendReceiptOnOnlinePayments: false,
        ...defaultValue,
        name: (defaultValue?.name ?? serviceName ?? '').trim(),
    }));

    // Reset data when modal opens with new service
    useEffect(() => {
        if (serviceName && defaultValue) {
            setData({
                ...defaultValue,
                name: (defaultValue.name ?? serviceName ?? '').trim(),
                category: defaultValue.category ?? '',
                sendInvoiceBeforeAppointment: defaultValue.sendInvoiceBeforeAppointment ?? false,
                sendInvoiceDaysBeforeAppointment: defaultValue.sendInvoiceDaysBeforeAppointment ?? 3,
                sendReceiptOnPayment: defaultValue.sendReceiptOnPayment ?? false,
                sendReceiptOnAllPaymentMethods: defaultValue.sendReceiptOnAllPaymentMethods ?? true,
                sendReceiptOnPaymentMethods: defaultValue.sendReceiptOnPaymentMethods ?? [],
                sendReceiptOnOnlinePayments: defaultValue.sendReceiptOnOnlinePayments ?? false,
            });
        }
    }, [serviceName, defaultValue]);

    const id = useMemo(() => `service-${toDomSafeIdPart(serviceName)}`, [serviceName]);
    const normalizedCurrentName = (defaultValue?.name ?? serviceName ?? '').trim().toLocaleLowerCase();
    const nameAlreadyExists = useMemo(() => {
        const candidate = (data.name ?? '').trim() || fallbackServiceName;
        const normalizedCandidate = candidate.trim().toLocaleLowerCase();
        if (!normalizedCandidate) {
            return false;
        }

        return existingServiceNames.some(name => {
            const normalizedName = name.trim().toLocaleLowerCase();
            return normalizedName === normalizedCandidate && normalizedName !== normalizedCurrentName;
        });
    }, [data.name, existingServiceNames, fallbackServiceName, normalizedCurrentName]);
    const resolvedName = data.name?.trim() || fallbackServiceName;
    const canSave = !!serviceName && !!resolvedName && !nameAlreadyExists;

    return (
        <Modal size="lg" isOpen={!!serviceName}>
            <ModalHeader toggle={onClose}>
                {t(TK.serviceConfiguration)} - {defaultValue?.name || serviceName || t(TK.new)}
            </ModalHeader>
            <ModalBody>
                {data && (
                    <>
                        <AppTabs value={tab} onChange={(_, v) => setTab(v)} indicatorColor="primary" textColor="primary" style={{ marginBottom: 'var(--spacer-S)', borderBottom: 'var(--border-thin) solid var(--appLigthGrey)' }}>
                            <Tab label={t(TK.settings)} value="settings" />
                            <Tab label={t(TK.prices)} value="prices" />
                            {hasStockFeature && <Tab label={t(TK.stock)} value="stock" />}
                            {hasInvoicesFeature && <Tab label={t(TK.invoiceData)} value="invoices" />}
                        </AppTabs>

                        <div>
                            {tab === 'settings' && <ServiceConfigurationSettingsTab
                                id={id}
                                data={data}
                                rooms={rooms}
                                categories={categories}
                                nameAlreadyExists={nameAlreadyExists}
                                setData={setData}
                            />}

                            {tab === 'prices' && <ServiceConfigurationPricesTab
                                id={id}
                                data={data}
                                clientOrigins={clientOrigins}
                                setData={setData}
                            />}

                            {hasStockFeature && tab === 'stock' && <ServiceConfigurationStockTab
                                id={id}
                                data={data}
                                setData={setData}
                            />}

                            {hasInvoicesFeature && tab === 'invoices' && <ServiceConfigurationInvoicesTab
                                id={id}
                                data={data}
                                paymentMethods={paymentMethods}
                                setData={setData}
                            />}
                        </div>
                    </>
                )}

                <hr />

                <ButtonsContainer>
                    <AppButton
                        id={`${id}-delete-button`}
                        color="danger"
                        onClick={() => serviceName && onDelete(serviceName)}
                    >
                        {t(TK.delete)}
                    </AppButton>
                    <AppButton
                        id={`${id}-save-button`}
                        color="success"
                        disabled={!canSave}
                        onClick={() => serviceName && onSave(serviceName, {
                            ...data,
                            name: data.name?.trim() || fallbackServiceName,
                            category: data.category?.trim(),
                        })}
                    >
                        {t(TK.save)}
                    </AppButton>
                    <AppButton id={`${id}-cancel-button`} onClick={onClose}>
                        {t(TK.cancel)}
                    </AppButton>
                </ButtonsContainer>
            </ModalBody>
        </Modal>
    );
};

export default ServiceConfigurationModal;
